import type { Header } from '@/types/table/table';
import type { ClientViewId } from '../types/types';

export const CLIENTS_ROUTE_PATH = '/private/clients';

/**
 * Clave de configuración de columnas en `/user/api/config_users` (DsDataTable,
 * mismo mecanismo que `POLICY_TABLE_NAME`). Se versiona (como Pólizas) porque
 * la tabla anterior era "a mano" (sin persistencia de servidor) y la key de
 * columna cambió de `name` a `legal_name`: una tabla vieja en `config_users`
 * con `key: 'name'` no debe mezclarse con esta ni heredar un orden inválido.
 * `_alias`: se agregó la columna "Alias" justo después de "Cliente"; una
 * config guardada sin ella la mandaría al final (las columnas nuevas se
 * añaden al final al mezclar), así que se versiona de nuevo.
 */
export const CLIENT_TABLE_NAME = 'clients_v2_shared_table_2026_10_alias';
// Alias retrocompatible: algunos imports tempranos del módulo usaban este
// nombre antes de adoptar la tabla compartida (DsDataTable).
export const CLIENTS_TABLE_NAME = CLIENT_TABLE_NAME;

/**
 * Las 5 columnas EXACTAS del rediseño (`entrega-vue/src/logic/clientes.js`),
 * ahora con el tipo `Header` que consume `DsDataTable` (persistencia real en
 * servidor vía `useConfigHeader`, igual que Pólizas). `key: 'legal_name'`
 * reemplaza al `name` anterior porque es el campo real que el backend ordena
 * (`buildFilterParams` → `order`, allow-list de `get_clients_list`); las
 * celdas se pintan con slots `cell.<key>` (favorito+nombre, contacto, avisos,
 * expediente), no por `item[header.key]` plano.
 */
export const CLIENT_HEADERS: Header[] = [
    { key: 'legal_name', title: 'Cliente', width: 250, minWidth: '180px', maxWidth: '420px', sortable: true, show: true, lockShow: true },
    { key: 'alias', title: 'Alias', width: 160, minWidth: '120px', maxWidth: '320px', sortable: true, show: true, lockShow: true },
    { key: 'contact', title: 'Contacto', width: 190, minWidth: '160px', maxWidth: '360px', sortable: true, show: true, lockShow: true },
    { key: 'policyCount', title: 'Pólizas', width: 78, minWidth: '72px', maxWidth: '180px', sortable: true, show: true, lockShow: true, align: 'end' },
    { key: 'billingStatus', title: 'Avisos de pago', width: 128, minWidth: '110px', maxWidth: '240px', sortable: true, show: true, lockShow: true },
    { key: 'expedientePercent', title: 'Expediente', width: 122, minWidth: '110px', maxWidth: '220px', sortable: true, show: true, lockShow: true }
];

// El endpoint de exportación no acepta una lista de columnas: el backend genera
// este conjunto fijo. Se conserva aquí como contrato visible para la UI/pruebas.
export const CLIENT_EXPORT_COLUMNS = ['Nombre', 'RFC', 'Tipo de persona', 'Alias', 'Teléfono', 'Correo electrónico', 'Aviso de pago', 'Cantidad de pólizas'] as const;

export const CLIENT_VIEWS: Array<{ id: ClientViewId; label: string; tone?: 'toca' | 'pylc' | 'entregado' | 'fuera'; isAll?: boolean }> = [
    // Orden del rediseño (clientes.js VISTAS): Favoritos primero.
    { id: 'favorites', label: 'Favoritos', tone: 'entregado' },
    { id: 'all', label: 'Todos mis clientes', tone: 'pylc', isAll: true },
    { id: 'incomplete', label: 'Datos por completar', tone: 'toca' },
    { id: 'without-billing', label: 'Sin avisos de pago', tone: 'fuera' }
];

export const CLIENT_FILTER_OPTIONS = {
    type: [
        { value: '2', label: 'Persona moral' },
        { value: '1', label: 'Persona física' }
    ],
    billing: [
        { value: '1', label: 'Activado' },
        { value: '0', label: 'No activado' },
        { value: '-1', label: 'No disponible' }
    ]
};

export const EXPEDIENTE_LABELS = {
    first_name: 'el nombre',
    rfc: 'el RFC',
    curp: 'la CURP',
    birthdate: 'la fecha de nacimiento',
    email: 'el correo',
    phone: 'el teléfono',
    address: 'el domicilio',
    // Campos del expediente de persona moral (entity_type = 2). Forman parte del
    // catálogo de completitud igual que los físicos (ver calculateExpediente).
    legal_name: 'la razón social',
    tax_regime: 'el régimen fiscal',
    legal_representative: 'el representante legal',
    contact_person_name: 'la persona de contacto',
    contact_person_position: 'el puesto'
} as const;

export const CLIENT_ENTITY_TYPE_1_REQUIRED_FIELDS: (keyof typeof EXPEDIENTE_LABELS)[] =  ['first_name', 'rfc', 'curp', 'birthdate', 'email', 'phone', 'address'];
// NOTA (hoy): `surname1`/`surname2` NO se agregan aquí a propósito, aunque la
// UI de ClientDetail.vue (identityFields) ahora SIEMPRE los muestra y los
// marca `required: true` como campos visuales independientes. Este catálogo
// decide el % de completitud/"Faltan N datos", y ese cálculo tiene que seguir
// coincidiendo con el backend (`has_name`, Brokerin_client.php:864 y
// `$denominator` en :869 = 7 para física): el backend cuenta "el nombre" como
// UN SOLO requisito combinado (first_name+surname1+surname2 concatenados, no
// vacío), no como 3 independientes. Si se agregaran aquí, el denominador
// pasaría de 7 a 9 y el % del front dejaría de coincidir con el que manda el
// API (expediente_percent/expediente_missing) -- el mismo tipo de desfase
// lista-vs-detalle ya documentado como bug real (DEMO991123JUA). Además, un
// cliente física real sin apellido materno (caso legítimo y común en MX)
// nunca llegaría a 100% con el backend actual. physicalPersonNameForCompleteness
// (utils/clients.ts) sigue resolviendo "el nombre" con el mismo criterio
// combinado del backend. CONTRADICCIÓN DE REGLA reportada al coordinador:
// si el negocio quiere que los 3 campos cuenten independiente también en el
// %, hace falta ajustar `has_name` en el backend primero (compartido con
// ejecutivo) para no desincronizar front/back.

export const CLIENT_ENTITY_TYPE_2_REQUIRED_FIELDS: (keyof typeof EXPEDIENTE_LABELS)[] = ['legal_name', 'rfc', 'email', 'phone', 'address'];